// La venta del enlace `?sale=` que todavía no llegó entre las páginas cargadas: se lee sola por id.
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/api/errors';
import { wizardApi } from '@/api/services/retail-wizard';
import { PORTAL_KEYS, toRecentSales, type RecentSale } from '../../api/usePortal';

/** El silo responde 404 si la venta no existe y 403 si es de otra tienda: para el vendedor, no está. */
const NOT_FOUND_STATUSES = new Set([403, 404]);

export interface LinkedSaleResult {
    sale: RecentSale | null;
    isLoading: boolean;
    /** El silo dijo que la venta no es del vendedor. */
    notFound: boolean;
    /** La lectura falló por otra causa (5xx, red): se puede reintentar. */
    failed: boolean;
    retry: () => void;
}

/** `saleId` en `null` apaga la lectura: la venta ya está en la tabla o no hay enlace. */
export function useLinkedSale(saleId: string | null): LinkedSaleResult {
    const query = useQuery({
        queryKey: [...PORTAL_KEYS.recentSales, 'linked', saleId] as const,
        queryFn: () => wizardApi.getSale(saleId ?? ''),
        enabled: saleId !== null,
        select: (sale) => toRecentSales([sale])[0] ?? null,
        retry: false,
    });
    const isNotFound =
        query.error instanceof ApiError && NOT_FOUND_STATUSES.has(query.error.httpStatus);
    const active = saleId !== null;
    return {
        sale: active ? (query.data ?? null) : null,
        isLoading: active && query.isPending && query.fetchStatus !== 'idle',
        notFound: active && query.isError && isNotFound,
        failed: active && query.isError && !isNotFound,
        retry: () => {
            void query.refetch();
        },
    };
}
